import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../lib/LanguageContext";
import { CONTACT } from "../lib/translations";
import EnquiryForm from "../components/EnquiryForm";
import {
  GraduationCap, ShieldCheck, Trophy, HeartHandshake, Sparkles,
  BookOpen, Laptop, Dumbbell, Camera, Video,
  ArrowRight, Star, ChevronRight, ChevronLeft, Phone, Quote,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const trustIcons = [GraduationCap, ShieldCheck, Trophy, HeartHandshake, Sparkles];
const facIcons = [Laptop, BookOpen, Video, Dumbbell, ShieldCheck, Sparkles];
const actIcons = [Trophy, Camera, BookOpen, HeartHandshake];

const HERO_IMG_1 = "/site/hero-1.jpg";
const HERO_IMG_2 = "/site/hero-2.jpg";
const HERO_IMG_3 = "/site/hero-3.jpg";
const HERO_IMG_4 = "/site/hero-4.jpg";
const ABOUT_IMG = "/site/legacy.png";
const ACT_IMG = "/site/activities-section.png";

const Home = () => {
  const { t, lang } = useLang();
  const [tIdx, setTIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTIdx((p) => (p + 1) % t.testimonials.list.length), 6000);
    return () => clearInterval(id);
  }, [t.testimonials.list.length]);

  return (
    <div data-testid="home-page">
      {/* ===================== HERO ===================== */}
      <section className="relative pt-28 md:pt-32 pb-16 md:pb-20 overflow-hidden" data-testid="hero-section">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-school-secondary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-school-primary/10 blur-3xl pointer-events-none" />

        <div className="container-school relative grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left - text */}
          <div className="lg:col-span-6 animate-fade-up">
            <span className="eyebrow" data-testid="hero-eyebrow">{t.hero.eyebrow}</span>
            <h1 className="mt-4 font-heading font-extrabold text-4xl sm:text-5xl lg:text-[64px] leading-[1.1] text-school-text">
              {t.hero.headlineA}{" "}
              <span className="text-school-primary">{t.hero.headlineB}</span>
              <br />
              <span className="devanagari-accent">{t.hero.headlineC}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-school-muted max-w-xl leading-relaxed">{t.hero.sub}</p>

            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/contact#enquiry" className="btn-primary" data-testid="hero-cta-enquiry">
                {t.hero.ctaPrimary} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="hero-cta-visit">
                {t.hero.ctaSecondary}
              </Link>
            </div>

            {/* Stats strip */}
            <div className="mt-12 grid grid-cols-3 gap-3 md:gap-6 max-w-xl" data-testid="hero-stats">
              {[
                { num: "४६+", numEn: "46+", lbl: t.hero.statYears },
                { num: "५०००+", numEn: "5000+", lbl: t.hero.statStudents },
                { num: "९१%", numEn: "91%", lbl: t.hero.statResult },
              ].map((s, i) => (
                <div key={i} className="text-center md:text-left">
                  <div className="font-heading font-extrabold text-2xl md:text-3xl text-school-primary">
                    {lang === "mr" ? s.num : s.numEn}
                  </div>
                  <div className="text-xs md:text-sm text-school-muted mt-1 leading-snug">{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - bento image collage with 4 hero images */}
          <div className="lg:col-span-6 relative">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[460px] md:h-[520px] lg:h-[560px]">
              <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden relative shadow-xl animate-fade-up delay-100">
                <img src={HERO_IMG_1} alt="School atmosphere" loading="eager" className="w-full h-full object-cover" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-school-accent flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-sm font-heading font-semibold text-school-text leading-tight">
                    {lang === "mr" ? "सुरक्षित व शिस्तबद्ध परिसर" : "Safe & Disciplined Campus"}
                  </div>
                </div>
              </div>
              <div className="col-span-2 row-span-2 rounded-3xl overflow-hidden shadow-lg animate-fade-up delay-200">
                <img src={HERO_IMG_2} alt="Classroom" loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-2 row-span-2 rounded-3xl overflow-hidden shadow-lg animate-fade-up delay-300">
                <img src={HERO_IMG_3} alt="Activity" loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-6 row-span-2 rounded-3xl overflow-hidden shadow-lg animate-fade-up delay-400 relative">
                <img src={HERO_IMG_4} alt="Students" loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-school-primary/85 via-school-primary/30 to-transparent" />
                <div className="absolute inset-0 flex items-center px-6">
                  <div className="max-w-[60%]">
                    <Quote className="w-7 h-7 text-school-secondary mb-2" />
                    <div className="font-heading text-sm md:text-base font-semibold text-white leading-snug">{t.vision}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TRUST HIGHLIGHTS ===================== */}
      <section className="section-pad bg-school-muted/50" data-testid="trust-section">
        <div className="container-school">
          <div className="max-w-3xl">
            <span className="eyebrow">{t.trust.eyebrow}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-school-text leading-tight">
              {t.trust.title}
            </h2>
            <p className="mt-4 text-base md:text-lg text-school-muted">{t.trust.sub}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.trust.items.map((it, i) => {
              const Icon = trustIcons[i] || Sparkles;
              return (
                <div key={i} className="card-soft p-7" data-testid={`trust-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-school-secondary/15 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-school-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-school-text">{it.title}</h3>
                  <p className="mt-2 text-sm md:text-base text-school-muted leading-relaxed">{it.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== ABOUT PREVIEW ===================== */}
      <section className="section-pad" data-testid="about-preview-section">
        <div className="container-school grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <img src={ABOUT_IMG} alt="About" className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
            <div className="absolute -bottom-6 -right-6 bg-school-secondary rounded-2xl p-5 shadow-xl max-w-[200px] hidden md:block">
              <div className="font-heading font-extrabold text-3xl text-school-text">४६+</div>
              <div className="text-sm text-school-text font-semibold mt-1">
                {lang === "mr" ? "वर्षांचा शैक्षणिक वारसा" : "Years of legacy"}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="eyebrow">{t.aboutPrev.eyebrow}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-school-text leading-tight">
              {t.aboutPrev.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-school-muted leading-relaxed">{t.aboutPrev.p1}</p>
            <p className="mt-4 text-base md:text-lg text-school-muted leading-relaxed">{t.aboutPrev.p2}</p>
            <Link to="/about" data-testid="about-read-more" className="mt-6 inline-flex items-center gap-2 text-school-primary font-heading font-semibold hover:gap-3 transition-all">
              {t.aboutPrev.readMore} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== FACILITIES PREVIEW ===================== */}
      <section className="section-pad bg-school-primary text-white relative overflow-hidden" data-testid="facilities-preview-section">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-school-secondary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="container-school relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="eyebrow !text-school-secondary">{t.facPrev.eyebrow}</span>
              <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
                {t.facPrev.title}
              </h2>
              <p className="mt-4 text-base md:text-lg text-white/90">{t.facPrev.sub}</p>
            </div>
            <Link to="/facilities" data-testid="facilities-view-all" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-school-secondary text-school-text font-heading font-semibold hover:bg-[var(--school-secondary-dark)] transition-colors">
              {lang === "mr" ? "सर्व सुविधा पहा" : "View All Facilities"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.facPrev.items.map((it, i) => {
              const Icon = facIcons[i] || BookOpen;
              return (
                <div key={i} className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-sm hover:bg-white/15 transition-all" data-testid={`facility-card-${i}`}>
                  <Icon className="w-8 h-8 text-school-secondary" />
                  <h3 className="mt-4 font-heading font-bold text-lg text-white">{it.title}</h3>
                  <p className="mt-2 text-sm text-white/90 leading-relaxed">{it.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== ACTIVITIES PREVIEW ===================== */}
      <section className="section-pad" data-testid="activities-preview-section">
        <div className="container-school">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="eyebrow">{t.actPrev.eyebrow}</span>
              <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-school-text leading-tight">
                {t.actPrev.title}
              </h2>
              <p className="mt-4 text-base md:text-lg text-school-muted">{t.actPrev.sub}</p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {t.actPrev.items.map((it, i) => {
                  const Icon = actIcons[i] || Trophy;
                  return (
                    <div key={i} className="flex gap-4 p-4 rounded-2xl border border-school bg-white hover:border-school-accent transition-all">
                      <div className="w-11 h-11 rounded-xl bg-school-accent/10 text-school-accent flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-base text-school-text">{it.title}</h3>
                        <p className="mt-1 text-sm text-school-muted">{it.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Link to="/activities" data-testid="activities-view-all" className="mt-8 inline-flex items-center gap-2 text-school-primary font-heading font-semibold hover:gap-3 transition-all">
                {lang === "mr" ? "सर्व उपक्रम पहा" : "Explore all activities"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <img src={ACT_IMG} alt="Activities" className="rounded-3xl w-full h-[480px] object-cover shadow-xl" />
              <div className="absolute top-6 left-6 bg-white rounded-2xl p-4 shadow-xl">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-school-secondary" fill="currentColor" />
                  <span className="font-heading font-bold text-school-text">4.9 / 5</span>
                </div>
                <div className="text-xs text-school-muted mt-1">{lang === "mr" ? "पालकांचे मत" : "Parent rating"}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== ACHIEVEMENTS ===================== */}
      <section className="section-pad bg-school-muted/40" data-testid="achievements-preview-section">
        <div className="container-school">
          <div className="max-w-3xl">
            <span className="eyebrow">{t.achPrev.eyebrow}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-school-text leading-tight">{t.achPrev.title}</h2>
            <p className="mt-4 text-base md:text-lg text-school-muted">{t.achPrev.sub}</p>
          </div>

          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {t.achPrev.stats.map((s, i) => (
              <div key={i} className="card-soft p-6 text-center" data-testid={`achievement-stat-${i}`}>
                <div className="font-heading font-extrabold text-3xl md:text-4xl text-school-primary">{s.num}</div>
                <div className="mt-2 text-xs md:text-sm text-school-muted">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {t.achPrev.toppers.map((tp, i) => (
              <div key={i} className="relative card-soft p-6 overflow-hidden" data-testid={`topper-card-${i}`}>
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-school-secondary/20" />
                <Trophy className="w-10 h-10 text-school-secondary" />
                <h3 className="mt-3 font-heading font-bold text-lg text-school-text">{tp.name}</h3>
                <div className="mt-1 text-2xl font-heading font-extrabold text-school-primary">{tp.marks}</div>
                <p className="mt-2 text-sm text-school-muted">{tp.note}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/achievements" data-testid="achievements-view-all" className="btn-outline">
              {lang === "mr" ? "सर्व यशोगाथा पहा" : "See All Achievements"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section className="section-pad" data-testid="testimonials-section">
        <div className="container-school">
          <div className="max-w-3xl">
            <span className="eyebrow">{t.testimonials.eyebrow}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-school-text leading-tight">{t.testimonials.title}</h2>
          </div>
          <div className="mt-10 relative">
            <div className="card-soft p-8 md:p-12 max-w-4xl mx-auto relative overflow-hidden" data-testid="testimonial-card">
              <Quote className="absolute top-6 right-6 w-16 h-16 text-school-secondary/15" />
              <div className="flex gap-1 text-school-secondary">
                {Array.from({ length: 5 }).map((_, i) => (<Star key={i} className="w-5 h-5" fill="currentColor" />))}
              </div>
              <p className="mt-5 text-lg md:text-2xl font-heading font-medium text-school-text leading-relaxed">
                &ldquo;{t.testimonials.list[tIdx].quote}&rdquo;
              </p>
              <div className="mt-6 flex items-center justify-between">
                <div>
                  <div className="font-heading font-bold text-base text-school-primary">{t.testimonials.list[tIdx].author}</div>
                  <div className="text-sm text-school-muted">{t.testimonials.list[tIdx].role}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setTIdx((p) => (p - 1 + t.testimonials.list.length) % t.testimonials.list.length)} data-testid="testimonial-prev" aria-label="Previous" className="w-10 h-10 rounded-full border border-school flex items-center justify-center hover:bg-school-primary hover:text-white hover:border-school-primary transition-all">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={() => setTIdx((p) => (p + 1) % t.testimonials.list.length)} data-testid="testimonial-next" aria-label="Next" className="w-10 h-10 rounded-full border border-school flex items-center justify-center hover:bg-school-primary hover:text-white hover:border-school-primary transition-all">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-center gap-2">
              {t.testimonials.list.map((_, i) => (
                <button key={i} onClick={() => setTIdx(i)} data-testid={`testimonial-dot-${i}`} aria-label={`Testimonial ${i+1}`}
                  className={`h-2 rounded-full transition-all ${tIdx === i ? "w-8 bg-school-primary" : "w-2 bg-school-border"}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== ENQUIRY FORM ===================== */}
      <section id="enquiry" className="section-pad bg-school-primary relative overflow-hidden" data-testid="enquiry-section">
        <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-school-secondary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="container-school relative grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <span className="eyebrow !text-school-secondary">{t.enquiry.eyebrow}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-tight">{t.enquiry.title}</h2>
            <p className="mt-5 text-base md:text-lg text-white/90">{t.enquiry.sub}</p>
            <div className="mt-8 space-y-3">
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="call-us-link" className="flex items-center gap-3 text-white hover:text-school-secondary transition-colors">
                <Phone className="w-5 h-5 text-school-secondary" />
                <span className="font-heading font-semibold text-lg">{lang === "mr" ? CONTACT.phonePrimary : CONTACT.phonePrimaryEn}</span>
              </a>
              <a href={`tel:${CONTACT.phoneRawSecondary}`} data-testid="call-us-link-2" className="flex items-center gap-3 text-white hover:text-school-secondary transition-colors">
                <Phone className="w-5 h-5 text-school-secondary" />
                <span className="font-heading font-semibold text-lg">{lang === "mr" ? CONTACT.phoneSecondary : CONTACT.phoneSecondaryEn}</span>
              </a>
            </div>
          </div>
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl">
            <EnquiryForm />
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="section-pad" data-testid="faq-section">
        <div className="container-school max-w-4xl">
          <div className="text-center">
            <span className="eyebrow">{t.faq.eyebrow}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-school-text leading-tight">{t.faq.title}</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {t.faq.items.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="card-soft !px-6 !border" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="font-heading font-semibold text-base md:text-lg text-school-text hover:text-school-primary text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-school-muted text-base leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Home;
